import { AUTH_ERRORS } from "@/common/errors";
import { signInUserWithEmail } from "@/services/auth";
import { resolveMFAChallenge, verify } from "@/services/auth2FA";
import { useAuthStore } from "@/store/auth.store";
import { FirebaseError } from "firebase/app";
import type { MultiFactorError, MultiFactorResolver } from "firebase/auth";
import { useRef, useState } from "react";

const useAuth2FA = (): {
  requestSMSCode: (email: string, password: string) => Promise<void>;
  handleEnterCode: (code: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  showTwoFactorForm: boolean;
} => {
  const { signInWithCurrentUser } = useAuthStore.getState();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showTwoFactorForm, setShowTwoFactorForm] = useState<boolean>(false);
  const resolver = useRef<MultiFactorResolver | null>(null);
  const verificationId = useRef<string | null>(null);

  const requestSMSCode = async (
    email: string,
    password: string
  ): Promise<void> => {
    setShowTwoFactorForm(true);
    try {
      await signInUserWithEmail({ email, password });
    } catch (error) {
      if (error instanceof FirebaseError) {
        setError(error.code);
        if (error.code === AUTH_ERRORS.MULTI_FACTOR_REQUIRED) {
          const result = await resolveMFAChallenge(error as MultiFactorError);
          resolver.current = result.resolver;
          verificationId.current = result.verificationId;
        }
      }
    }
  };

  const handleEnterCode = async (code: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (resolver.current && verificationId.current) {
        await verify(resolver.current, verificationId.current, code);
        void signInWithCurrentUser();
        setShowTwoFactorForm(false);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        setError(error.code);
      }
    } finally {
      setLoading(false);
    }
  };

  return { handleEnterCode, requestSMSCode, loading, error, showTwoFactorForm };
};

export default useAuth2FA;
